import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaPhone } from 'react-icons/fa';
import { IoMdMail } from 'react-icons/io';

import styled from 'styled-components';

import { classifiedJoin, Contact } from '@/types/contact';
import { getFullName } from '@/utils/name';
import { AnchorLike } from '@/components/shared/AnchorLike';

type Props = {
  contact: Contact | null;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setModalMode?: React.Dispatch<React.SetStateAction<'new' | 'edit'>>;
  setContactToEdit?: React.Dispatch<React.SetStateAction<Contact | undefined>>;
};

export const Preview: React.FC<Props> = ({
  contact,
  setIsOpen,
  setModalMode,
  setContactToEdit,
}) => {
  const { t } = useTranslation();

  if (!contact) {
    return null;
  }

  return (
    <Wrapper>
      {contact && (
        <div>
          {setIsOpen !== undefined && (
            <ChangeButton
              onClick={() => {
                setIsOpen(true);
                if (setModalMode !== undefined) {
                  setModalMode('edit');
                }

                if (setContactToEdit !== undefined) {
                  setContactToEdit(contact);
                }
              }}
            >
              {t('ui.subscription.contact.change')}
            </ChangeButton>
          )}
          <div className="paragraph">
            {classifiedJoin([contact.corporationName, contact.departmentName])}
          </div>
          <div className="paragraph">
            <div>{contact.postalCode}</div>
            <div>{contact.address}</div>
          </div>
          <div className="paragraph">
            <div>
              {getFullName(contact.firstName, contact.lastName)}
              {(contact.kanaFirstName || contact.kanaLastName) &&
                '（' +
                  getFullName(contact.kanaFirstName, contact.kanaLastName) +
                  '）'}
            </div>
            <div data-test="addressee">
              {contact.addressee &&
                t('ui.expression.careOf', { name: contact.addressee })}
            </div>
          </div>
          <div className="paragraph">
            <div className="icon-text">
              <FaPhone />
              {contact.phoneNumber}
            </div>
            <div data-test="email" className="icon-text">
              <IoMdMail />
              {Object.prototype.hasOwnProperty.call(contact, 'ccEmail') && (
                <span className="icon-complement">
                  {t('models.contact.toMark')}
                </span>
              )}
              {contact.email}
            </div>
            {contact.ccEmail && (
              <div data-test="email" className="icon-text">
                <IoMdMail />
                <span className="icon-complement">
                  {t('models.contact.ccMark')}
                </span>
                {contact.ccEmail}
              </div>
            )}
          </div>
        </div>
      )}
    </Wrapper>
  );
};

const ChangeButton = styled(AnchorLike)`
  display: block;
  float: right;
`;

const Wrapper = styled.div`
  background: rgb(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 32px;
  margin-top: 16px;

  a.right {
    display: block;
    float: right;
  }

  [data-test='addressee'] {
    word-break: break-all;
  }
  .icon-complement {
    padding-right: 10px;
  }

  .icon-text {
    display: flex;
    align-items: center;
    gap: 3.5px;
  }
`;
